import type { ChannelCapabilities } from "@zapir/shared";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";
import { MessageMedia, type LocalMedia } from "./MessageMedia";
import { formatTime, messageAt, senderColorVar } from "@/lib/format";
import { cx } from "@/lib/cx";
import { useLongPress } from "@/hooks/useTouchGestures";
import type { TimelineMessage } from "@/lib/api";

export interface QuotedInfo {
  body: string;
  author: string;
  outgoing: boolean;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

interface BubbleProps {
  message: TimelineMessage;
  capabilities: ChannelCapabilities;
  quoted?: QuotedInfo | null;
  authorName?: string;
  /** Conversa de grupo: abre a calha do avatar a esquerda das recebidas. */
  inGroup?: boolean;
  /** Nome de quem enviou (so em grupos, so entrantes) — colorido por usuario. */
  senderName?: string | null;
  /** Foto de quem enviou (so em grupos, so entrantes). */
  senderAvatarUrl?: string | null;
  groupFirst?: boolean;
  groupLast?: boolean;
  /** Estado do outbox (envio otimista) — so existe em bolhas pendentes. */
  pendingStatus?: "pending" | "sending" | "failed";
  /** Mídia local da bolha otimista (object URL até confirmar). */
  localMedia?: LocalMedia | null;
  /** Progresso do upload em andamento (bolha otimista de mídia). */
  uploadProgress?: UploadProgress | null;
  onCancelUpload?: () => void;
  onRetry?: () => void;
  onReply?: () => void;
  /** Abre a foto no visualizador de tela cheia. */
  onOpenPhoto?: () => void;
  /** Encaminhar para outras conversas. */
  onForward?: () => void;
  /** Toque longo (dedo): abre a folha de acoes da mensagem. */
  onLongPress?: () => void;
  /** Apagar para todos. Ausente em pendentes, notas e ja apagadas. */
  onDelete?: () => void;
}

const MEDIA_TYPES = new Set(["image", "video", "audio", "voice", "document", "sticker", "location", "contact"]);

/**
 * Bolha no padrao Telegram Web K (doc 09 §3.2). O ack so aparece quando
 * `capabilities.ack` (RF-UX-120). Nota interna nunca vai ao cliente
 * (RF-UX-038) e ganha tratamento proprio "só equipe".
 */
export function Bubble({
  message,
  capabilities,
  quoted,
  authorName,
  inGroup,
  senderName,
  senderAvatarUrl,
  groupFirst,
  groupLast,
  pendingStatus,
  localMedia,
  uploadProgress,
  onCancelUpload,
  onRetry,
  onReply,
  onOpenPhoto,
  onForward,
  onLongPress,
  onDelete,
}: BubbleProps) {
  // Toque longo: 450ms parado. Rolar cancela — senao a folha abre no meio de
  // um arraste da timeline. Chamado ANTES de qualquer `return` para a ordem de
  // hooks nao mudar entre a bolha normal, a nota interna e a de sistema.
  const longPress = useLongPress(onLongPress);
  const isNote = message.isInternalNote === true;

  // Nota interna tambem chega como `type: "system"` (API), entao o
  // discriminador vem ANTES do tratamento de mensagens de sistema.
  if (isNote) {
    return (
      <div className="bubble-note" role="note" aria-label="Nota interna, o cliente não vê">
        <span className="note-label">
          <Icon name="lock" size={12} />
          Nota interna · só a equipe vê
        </span>
        <span className="note-body">{message.body}</span>
        <div className="bubble-meta">
          <span className="bubble-time">{formatTime(messageAt(message))}</span>
          {authorName ? <span className="bubble-time">· {authorName}</span> : null}
          {pendingStatus === "failed" && onRetry ? (
            <button type="button" className="bubble-fail" onClick={onRetry} aria-label="Tentar de novo">
              falha <Icon name="refresh" size={12} className="inline align-[-1px]" />
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (message.type === "system") {
    return (
      <div className="bubble-service" role="status">
        {message.body}
      </div>
    );
  }

  const outgoing = message.direction === "outbound";
  const failed = pendingStatus === "failed" || message.deliveryStatus === "failed";
  const hasMedia = MEDIA_TYPES.has(message.type);
  const mediaOnly = hasMedia && !message.body;
  const uploading =
    Boolean(localMedia) && (pendingStatus === "pending" || pendingStatus === "sending") && !failed;
  // Apagada no WhatsApp: a bolha continua na timeline, so esmaecida. O inbox
  // e registro de atendimento — apagar de verdade perderia a prova.
  const revoked = Boolean(message.revokedAt);
  // Em grupo o bico fica na PRIMEIRA bolha, apontando para o avatar que agora
  // esta no topo da pilha (WhatsApp). Em 1:1 segue na ultima, como no Telegram.
  const tailOnFirst = !outgoing && Boolean(inGroup);
  // Figurinha nao tem fundo, entao tambem nao tem bico: ele ficaria boiando
  // sozinho ao lado do desenho.
  const isSticker = message.type === "sticker";
  const hasTail = !isSticker && (tailOnFirst ? groupFirst : groupLast);

  const progressPct =
    uploadProgress && uploadProgress.total > 0
      ? Math.min(100, Math.round((uploadProgress.loaded / uploadProgress.total) * 100))
      : null;

  return (
    <div
      className={cx(
        "bubble-row",
        outgoing ? "out" : "in",
        groupFirst && "first",
        groupLast && "last",
        hasTail && "has-tail",
      )}
      {...longPress}
    >
      {/* Calha do avatar em grupo: existe em TODA a pilha para as bolhas nao
          dancarem, mas a foto so aparece na PRIMEIRA — a pilha cresce para
          baixo a partir de quem falou, como no WhatsApp. */}
      {!outgoing && inGroup ? (
        <span className="bubble-avatar" aria-hidden="true">
          {groupFirst ? <Avatar name={senderName ?? "?"} src={senderAvatarUrl} size={34} /> : null}
        </span>
      ) : null}
      {onReply && !failed ? (
        <button type="button" className="bubble-reply-btn" aria-label="Responder citando" onClick={onReply}>
          <Icon name="reply" size={17} />
        </button>
      ) : null}
      {onForward && !failed && !revoked ? (
        <button type="button" className="bubble-fwd-btn" aria-label="Encaminhar mensagem" onClick={onForward}>
          <Icon name="reply" size={16} className="-scale-x-100" />
        </button>
      ) : null}
      {onDelete && !failed && !revoked ? (
        <button type="button" className="bubble-del-btn" aria-label="Apagar mensagem" onClick={onDelete}>
          <Icon name="trash" size={16} />
        </button>
      ) : null}

      <div
        className={cx(
          "bubble",
          outgoing ? "out" : "in",
          groupFirst && "first",
          groupLast && "last",
          hasTail && "has-tail",
          tailOnFirst && "tail-top",
          hasMedia && "has-media",
          mediaOnly && "media-only",
          isSticker && "bubble-sticker",
          revoked && "revoked",
        )}
      >
        <div className="bubble-content">
          {revoked ? (
            <span className="bubble-revoked-tag">
              <Icon name="warning" size={12} /> apagada
            </span>
          ) : null}
          {message.forwarded ? (
            <span className="bubble-fwd-tag">
              <Icon name="reply" size={11} className="-scale-x-100" />
              Encaminhada
            </span>
          ) : null}
          {/* So na primeira da pilha: repetir o nome em cada bolha e o que o
              WhatsApp evita quando a mesma pessoa manda varias seguidas. */}
          {!outgoing && groupFirst && senderName ? (
            <span className="bubble-author" style={{ color: senderColorVar(senderName) }}>
              {senderName}
            </span>
          ) : null}
          {quoted ? (
            <div className="bubble-reply" role="quote">
              <span className="bubble-reply-author">{quoted.author}</span>
              <span className="bubble-reply-text">{quoted.body}</span>
            </div>
          ) : null}

          {hasMedia ? (
            <MessageMedia message={message} local={localMedia} onOpenPhoto={onOpenPhoto} />
          ) : (
            <span className="bubble-body">{message.body}</span>
          )}

          {uploading ? (
            <div
              className="bubble-upload"
              role="progressbar"
              aria-label={progressPct === null ? "Enviando anexo" : `Enviando anexo: ${progressPct}%`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPct ?? undefined}
            >
              <div className="bubble-upload-bar">
                <i style={progressPct === null ? undefined : { width: `${progressPct}%` }} />
              </div>
              <span className="bubble-upload-label">
                {progressPct === null ? "Enviando…" : `Enviando… ${progressPct}%`}
              </span>
              {onCancelUpload ? (
                <button
                  type="button"
                  className="bubble-upload-cancel"
                  onClick={onCancelUpload}
                  aria-label="Cancelar envio do anexo"
                >
                  <Icon name="close" size={14} />
                </button>
              ) : null}
            </div>
          ) : null}

          <span className="bubble-meta">
            {pendingStatus === "pending" || pendingStatus === "sending" ? (
              <Icon name="clock" size={11} className="opacity-70" />
            ) : null}
            <span className="bubble-time">{formatTime(messageAt(message))}</span>
            {failed ? (
              onRetry ? (
                <button type="button" className="bubble-fail" onClick={onRetry} aria-label="Tentar enviar de novo">
                  falha <Icon name="refresh" size={12} className="inline align-[-1px]" />
                </button>
              ) : null
            ) : capabilities.ack && outgoing ? (
              <Ack level={message.deliveryStatus} />
            ) : null}
          </span>
        </div>

        {hasTail ? <BubbleTail /> : null}
      </div>
    </div>
  );
}

/** Tiques de entrega. Exportado porque a lista de conversas usa o mesmo. */
export function Ack({ level }: { level: string }) {
  if (level === "sent") {
    return (
      <svg className="bubble-check" viewBox="0 0 20 12" fill="none" aria-label="Enviada" role="img">
        <path d="M3 6.4 6.4 9.8 16.5 2.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (level === "delivered" || level === "read" || level === "played") {
    const read = level === "read" || level === "played";
    return (
      <svg
        className={cx("bubble-check", read && "read")}
        viewBox="0 0 22 12"
        fill="none"
        style={read ? { opacity: 1 } : { opacity: 0.72 }}
        aria-label={read ? "Lida" : "Entregue"}
        role="img"
      >
        <path d="M1.5 6.4 4.9 9.8 14.4 2.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.5 6.4 11.9 9.8 21 2.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return null;
}

/** Tail real do Telegram (`#message-tail-filled`, 11x20). */
function BubbleTail() {
  return (
    <svg className="bubble-tail" viewBox="0 0 11 20" width="11" height="20" aria-hidden="true" focusable="false">
      <g transform="translate(9 -14)" fill="inherit" fillRule="evenodd">
        <path
          d="M-6 16h6v17c-.193-2.84-.876-5.767-2.05-8.782-.904-2.325-2.446-4.485-4.625-6.48A1 1 0 01-6 16z"
          transform="matrix(1 0 0 -1 0 49)"
        />
      </g>
    </svg>
  );
}
